import React, { useState } from 'react';
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Divider } from "primereact/divider";
import { Tooltip } from "primereact/tooltip";
import { classNames } from 'primereact/utils';
import PageHeading from "../../../../Components/PageHeading";
import Label from "../../../../Components/Label";

type ProfileData = {
    name: string;
    cell: string;
    mobile: string;
    email: string;
    contactName: string;
    contactCell: string;
    contactEmail: string;
    address: string;
    city: string;
    postOffice: string;
    postCode: string;
    thana: string;
    district: string;
};

const Profile = () => {
    const [isEditing, setIsEditing] = useState(false);

    const [formData, setFormData] = useState<ProfileData>({
        name: "M2M Communications Ltd.",
        cell: "+88018100000",
        mobile: "+88018100000",
        email: "test@selopia.com",
        contactName: "John Doe",
        contactCell: "+88018100000",
        contactEmail: "test@selopia.com",
        address: "Concord tower, Bangla Motor, Dhaka",
        city: "Dhaka",
        postOffice: "Dhaka",
        postCode: "5400",
        thana: "Shahbag",
        district: "Dhaka",
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, field: string) => {
        setFormData({ ...formData, [field]: e.target.value });
    };

    const handleSave = () => setIsEditing(false);
    const handleCancel = () => setIsEditing(false);

    const renderField = (label: string, fieldName: keyof ProfileData, fullTitle?: string) => {
        const isAddress = fieldName === 'address';

        return (
            <div className="flex align-items-start mb-1 w-full" >
                {/* 1. Label Section: Fixed width adjusted to reduce gap */}
                <div className="flex align-items-center  " style={{  width: '100px', flexShrink: 0, height: '38px' }}>
                    <span className="text-sm text-500 font-semibold uppercase tracking-wider">
                        {label}
                    </span>
                    {/* 2. Question Mark Tooltip for abbreviated labels */}
                    {fullTitle && (
                        <i
                            className="pi pi-question-circle ml-1 text-400 cursor-pointer text-xs custom-tooltip-icon"
                            data-pr-tooltip={fullTitle}
                            data-pr-position="top"
                        />
                    )}
                </div>

                <div className="flex-grow-1 flex align-items-center ">
                    {isEditing ? (
                        isAddress ? (
                            <InputTextarea
                                value={formData[fieldName]}
                                onChange={(e) => handleChange(e, fieldName)}
                                rows={2}
                                autoResize
                                className="w-full p-inputtext-sm"
                            />
                        ) : (
                            <InputText
                                value={formData[fieldName]}
                                onChange={(e) => handleChange(e, fieldName)}
                                className="p-inputtext-sm w-full"
                                style={{ height: '36px' }}
                            />
                        )
                    ) : (
                        <span
                            className="text-900 font-semibold w-full flex align-items-center"
                            style={{
                                minHeight: '36px',
                                padding: '0.429rem 0.5rem', // Reduced horizontal padding
                                border: '1px solid transparent',
                                whiteSpace: isAddress ? 'normal' : 'nowrap',
                                overflow: isAddress ? 'visible' : 'hidden',
                                textOverflow: isAddress ? 'unset' : 'ellipsis'
                            }}
                        >
                            {formData[fieldName]}
                        </span>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="card p-4 shadow-1 border-round-xl bg-white">
            <Tooltip target=".custom-tooltip-icon" /> {/* Tooltip Initialization */}

            <PageHeading
                title="Profile Settings"
                isEdit={!isEditing}
                // back={true}
                editFunction={() => setIsEditing(true)}
            />

            <div className="mx-auto w-full" style={{ maxWidth: '900px' }}>
                {/* Profile Header */}
                <div className="flex flex-column align-items-center mb-5 mt-2">
                    <div className="relative">
                        <img className="h-7rem object-fit-cover" src="/logo-symbool.png" alt="Profile" />
                        <Button
                            icon={isEditing ? "pi pi-pencil" : "pi pi-camera"}
                            className={classNames(
                                "p-button-rounded absolute shadow-2 border-2 border-white",
                                { 'p-button-warning': isEditing, 'p-button-primary': !isEditing }
                            )}
                            style={{ bottom: '2px', right: '2px', width: '2.2rem', height: '2.2rem' }}
                            onClick={() => console.log("Upload Image")}
                        />
                    </div>
                    <h2 className="mt-3 mb-1 font-bold text-900 ">{formData.name}</h2>
                    <span className="text-500 text-sm">Corporate Account</span>
                </div>

                <Divider />

                <div className="p-2">
                    <div className="grid p-fluid">
                        <div className="col-12 md:col-6 lg:pr-4">
                            <div className="mb-4">
                                <Label label="Contact Information" />
                            </div>
                            {renderField("Name", "name")}
                            {renderField("Cell", "cell")}
                            {renderField("Mobile", "mobile")}
                            {renderField("Email", "email")}
                            {renderField("C. Name", "contactName", "Contact Person Name")}
                            {renderField("C. Cell", "contactCell", "Contact Person Cell")}
                            {renderField("C. Email", "contactEmail", "Contact Person Email")}
                        </div>

                        <div className="col-12 md:col-6 lg:pl-4">
                            <div className="mb-4">
                                <Label label="Address Details" />
                            </div>
                            {renderField("District", "district")}
                            {renderField("City", "city")}
                            {renderField("Thana", "thana")}
                            {renderField("P. Office", "postOffice")}
                            {renderField("P. Code", "postCode")}
                            {renderField("Address", "address")}
                        </div>
                    </div>

                    <div className="flex justify-content-end mt-4">
                        {isEditing && (
                            <div className="flex gap-2">
                                <Button label="Cancel" icon="pi pi-times" className="p-button-text p-button-secondary" onClick={handleCancel} />
                                <Button label="Update Profile" icon="pi pi-check" className="p-button-primary px-4 shadow-2" onClick={handleSave} />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Profile;